<template>
  <div v-if="fill" class="r-json-editor r-json-editor-fill">
    <div ref="editorContainer" class="r-json-editor-container r-json-editor-container-fill"></div>
    <span v-if="parseError" class="r-json-editor-error">JSON inválido: {{parseError}}</span>
  </div>
  <div v-else class="r-json-editor item-content item-input item-input-with-value">
    <div class="item-inner">
      <div v-if="label" class="item-title item-label item-label-up item-floating-label">
        {{label}}
      </div>
      <div class="item-input-wrap">
        <div ref="editorContainer" class="r-json-editor-container" :style="containerStyle"></div>
        <span v-if="parseError" class="r-json-editor-error">JSON inválido: {{parseError}}</span>
      </div>
    </div>
  </div>
</template>

<script>
import JSONEditor from "jsoneditor";
import "jsoneditor/dist/jsoneditor.min.css";

export default {
  name: "rJsonEditor",
  props: {
    name: { type: String, required: true },
    form: { type: Object, required: true },
    label: { type: String, default: "" },
    readonly: { type: Boolean, default: false },
    overrideReadonly: { type: Boolean, default: false },
    height: { type: [Number, String], default: 240 },
    fill: { type: Boolean, default: false },
    mode: { type: String, default: "tree" },
    modes: {
      type: Array,
      default: function () {
        return ["tree", "code", "view"];
      }
    },
    defaultValue: { type: [Object, Array], default: null }
  },
  data() {
    return {
      editor: null,
      parseError: null,
      lastSerialized: null,
      suppressEmit: false
    };
  },
  computed: {
    isReadonly() {
      const self = this;
      return self.overrideReadonly ? self.readonly : self.readonly || (self.form && self.form.readonly);
    },
    containerStyle() {
      const self = this;
      if (self.fill) return { height: "100%", width: "100%" };
      const h = typeof self.height === "number" ? self.height + "px" : self.height;
      return { height: h, width: "100%" };
    }
  },
  methods: {
    getValueFromForm() {
      const self = this;
      const path = self.name.split(".");
      let ctx = self.form && self.form.formData;
      for (let i = 0; i < path.length; i++) {
        if (ctx == null) return undefined;
        ctx = ctx[path[i]];
      }
      return ctx;
    },
    setValueInForm(val) {
      const self = this;
      const path = self.name.split(".");
      let ctx = self.form.formData;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (ctx[key] == null || typeof ctx[key] !== "object") {
          const next = path[i + 1];
          const nextIsIndex = /^\d+$/.test(next);
          self.$set(ctx, key, nextIsIndex ? [] : {});
        }
        ctx = ctx[key];
      }
      self.$set(ctx, path[path.length - 1], val);
      self.$emit("change", val, self);
    },
    onEditorChange() {
      const self = this;
      if (self.suppressEmit) return;
      try {
        const val = self.editor.get();
        self.parseError = null;
        self.lastSerialized = JSON.stringify(val);
        self.setValueInForm(val);
      } catch (e) {
        self.parseError = e.message || String(e);
      }
    },
    refreshFromForm() {
      const self = this;
      if (!self.editor) return;
      const val = self.getValueFromForm();
      const serialized = JSON.stringify(val == null ? null : val);
      if (serialized === self.lastSerialized) return;
      self.suppressEmit = true;
      try {
        self.editor.set(val == null ? {} : val);
        self.lastSerialized = serialized;
        if (typeof self.editor.expandAll === "function") {
          try { self.editor.expandAll(); } catch (e) { /* noop */ }
        }
      } finally {
        self.suppressEmit = false;
      }
    }
  },
  mounted() {
    const self = this;
    const initial = self.getValueFromForm();
    const value = initial != null ? initial : (self.defaultValue != null ? self.defaultValue : {});

    self.editor = new JSONEditor(self.$refs.editorContainer, {
      mode: self.isReadonly ? "view" : self.mode,
      modes: self.isReadonly ? ["view"] : self.modes,
      onChange: self.onEditorChange,
      onValidationError: function (errors) {
        self.parseError = errors && errors.length ? errors.map(e => e.message).join(" · ") : null;
      },
      mainMenuBar: true,
      navigationBar: false,
      statusBar: false,
      search: false
    });
    self.editor.set(value);
    self.lastSerialized = JSON.stringify(value);
    if (typeof self.editor.expandAll === "function") {
      try { self.editor.expandAll(); } catch (e) { /* noop */ }
    }
    if (initial == null && self.defaultValue != null) {
      self.setValueInForm(self.defaultValue);
    }
  },
  watch: {
    "form.formData": {
      deep: true,
      handler() {
        this.refreshFromForm();
      }
    },
    isReadonly(val) {
      if (this.editor) {
        this.editor.setMode(val ? "view" : this.mode);
      }
    }
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }
};
</script>

<style scoped>
.r-json-editor {
  display: block;
  width: 100%;
}
.r-json-editor-fill {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.r-json-editor-container-fill {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
}
.r-json-editor-container {
  width: 100%;
  min-height: 180px;
}
.r-json-editor-error {
  color: #c0392b;
  font-size: 11px;
  display: block;
  margin-top: 4px;
}
</style>
